/**
 * API Service
 *
 * Login is handled by Okta via the OktaAuth instance in `auth.ts`. Every
 * request is decorated with `Authorization: Bearer <accessToken>` so the
 * API Gateway JWT authorizer + Function URL Okta verification can identify
 * the caller and the backend's cross-tenant guards (consultantId checks)
 * can enforce ownership.
 */
import axios from 'axios';
import { getConfig } from './config';
import { getAccessTokenJwt, renewAccessTokenJwt } from '../auth';
import { acquireBusy, releaseBusy } from './busy';

function getApiClient() {
  const config = getConfig();
  // 60s default timeout for normal API calls. Without this axios will hang
  // forever on a stuck backend, freezing UI buttons in their loading state.
  // Upload + create-design call sites can override via dedicated client.
  const client = axios.create({
    baseURL: config.apiUrl,
    headers: { 'Content-Type': 'application/json' },
    timeout: 60_000,
  });

  // Inject Okta JWT on every outbound request. Without this header the
  // backend's API Gateway Okta authorizer returns 401 — the API isn't
  // reachable except through the portal wrapper. With the header, the
  // backend's _authenticated_user() can read the verified email and the
  // cross-tenant guards (e.g. handle_get_design's _enforce_design_ownership)
  // do their job.
  client.interceptors.request.use(async (cfg) => {
    try {
      const token = await getAccessTokenJwt();
      if (token) {
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      console.warn('[api] failed to attach Okta JWT:', err?.message || err);
    }
    return cfg;
  });

  // 401 retry: long sessions outlast the Okta access-token TTL. When a
  // request comes back 401, force-renew the token and retry the SAME
  // request once. The `_retry` flag stops us from looping forever if
  // the second 401 is a real auth failure (groups missing, etc.).
  client.interceptors.response.use(
    (resp) => resp,
    async (error) => {
      const cfg = error?.config;
      const status = error?.response?.status;
      if (status !== 401 || !cfg || cfg._retry) {
        return Promise.reject(error);
      }
      cfg._retry = true;
      try {
        const fresh = await renewAccessTokenJwt();
        if (!fresh) return Promise.reject(error);
        cfg.headers = cfg.headers || {};
        cfg.headers.Authorization = `Bearer ${fresh}`;
        return client.request(cfg);
      } catch (e) {
        return Promise.reject(error);
      }
    },
  );

  return client;
}

/**
 * Upload a document
 * Uses presigned URL for large files (> 5MB), direct upload for smaller files
 */
export async function uploadDocument(file, consultantId, consultantEmail, workflowId = null) {
  // Register in the global busy counter so the stale-chunk auto-reload
  // (main.tsx vite:preloadError handler) never yanks the page mid-upload.
  // Static import: a dynamic import() here becomes its own Vite chunk,
  // which 404s on a stale tab at the exact moment of upload — the guard
  // itself would trigger the reload it exists to prevent.
  acquireBusy();
  try {
    return await _uploadDocumentInner(file, consultantId, consultantEmail, workflowId);
  } finally {
    releaseBusy();
  }
}

async function _uploadDocumentInner(file, consultantId, consultantEmail, workflowId = null) {
  // Lowered from 5 MB → 4 MB on 2026-05-20 (F6 / feedback v3ifd7ml).
  // Base64 inflates the request ~33% — a 5 MB file became ~6.7 MB of
  // base64, and the backend's 6 MB Lambda cap rejected it as 413 even
  // though the source file was technically "small enough". 4 MB keeps
  // the post-encoding payload safely under the cap.
  const MAX_DIRECT_UPLOAD_SIZE = 4 * 1024 * 1024; // 4 MB
  const client = getApiClient();

  if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
    const presignedResponse = await client.post('/upload', {
      requestPresignedUrl: true,
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      consultantId,
      consultantEmail,
      workflowId,
    });
    const { documentId, s3Key, uploadUrl } = presignedResponse.data;
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed (HTTP ${uploadResponse.status})`);
    }
    return { documentId, s3Key, message: 'Document uploaded successfully' };
  }

  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = async () => {
      try {
        const base64Content = reader.result.split(',')[1];
        const response = await client.post('/upload', {
          fileName: file.name,
          fileContent: base64Content,
          fileType: file.type,
          consultantId,
          consultantEmail,
          workflowId,
        });
        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Coarse region from the browser timezone — the backend uses this only
 * when the Okta groups claim carries no region-suffixed group (which is
 * the norm on dev), so the usage dashboard's By-region view isn't all
 * "unknown".
 */
function regionHintFromTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    if (/^America\//.test(tz) || tz === 'Pacific/Honolulu') return 'AMER';
    if (/^(Europe|Africa)\//.test(tz) || tz === 'Atlantic/Reykjavik') return 'EMEA';
    if (/^(Asia|Australia|Pacific)\//.test(tz)) return 'APAC';
  } catch { /* Intl unavailable — skip the hint */ }
  return '';
}

export async function createDesign(documentId, brdS3Key, dataMappingS3Key, consultantId, consultantEmail, initialGuidance = '', designName = '', options = {}) {
  const client = getApiClient();
  const payload = { documentId, brdS3Key, consultantId, consultantEmail };
  const regionHint = regionHintFromTimezone();
  if (regionHint) payload.regionHint = regionHint;
  if (dataMappingS3Key) payload.dataMappingS3Key = dataMappingS3Key;
  // The user's first chat message becomes session-specific guidance the
  // design agent reads alongside the BRD. Without this, the typed input
  // is dropped on the floor and the pipeline runs with only the static
  // system prompt — every design ends up identical regardless of what
  // the user wanted to emphasize.
  if (initialGuidance && initialGuidance.trim()) {
    payload.initialGuidance = initialGuidance.trim().slice(0, 4000);
  }
  // Project name from the New Project modal. Without this, the sidebar
  // tree falls back to the BRD filename (long + ugly), and the modal's
  // "what should we call this?" input has no effect.
  if (designName && designName.trim()) {
    payload.designName = designName.trim().slice(0, 200);
  }
  // Project shells: the modal creates a project (name + KB only)
  // without a BRD. The backend allows this when projectShell:true and
  // marks the row status='project_shell'. The user attaches a BRD
  // later via a "+ New workflow" flow inside the project.
  if (options?.projectShell) {
    payload.projectShell = true;
  }
  // Workflow under existing project — the new row links to its parent
  // shell via projectId so the sidebar tree groups them under the same
  // project label. Phase 3a-lite hierarchy.
  if (options?.projectId) {
    payload.projectId = options.projectId;
  }
  // Chat-only / no-BRD path: caller pre-mints a workflowId so the
  // empty design row carries one (the agent's tools cache the
  // `{cid}/{wid}/…` prefix for any later uploads via the composer).
  if (options?.workflowId) {
    payload.workflowId = options.workflowId;
  }
  // Customer name — required field for new designs/projects.
  if (options?.customerName && options.customerName.trim()) {
    payload.customerName = options.customerName.trim();
  }
  const response = await client.post('/design/create', payload);
  return response.data;
}

export async function duplicateDesign(designId, consultantId) {
  const client = getApiClient();
  const response = await client.post('/design/create', {
    fromDesignId: designId,
    consultantId,
  });
  return response.data;
}

export async function getDesign(designId, version) {
  const client = getApiClient();
  const url = version
    ? `/design/${designId}?version=${encodeURIComponent(version)}`
    : `/design/${designId}`;
  const response = await client.get(url);
  return response.data;
}

/**
 * Fetch older chat messages for a design. get_design inlines only the last
 * 50 raw messages (chatHistoryTruncated/chatHistoryTotal flag the rest);
 * this pages backwards through the remainder.
 *
 * `before` is a raw-message index: returns up to `limit` messages ending at
 * index before-1. Response: { messages, total, hasMore }.
 */
export async function fetchDesignChat(designId, { limit = 50, before } = {}) {
  const client = getApiClient();
  const params = { limit };
  if (before !== undefined) params.before = before;
  const response = await client.get(`/design/${designId}/chat`, { params });
  return response.data;
}

/**
 * Fetch a fresh presigned URL for any file under the design's workflow
 * path. Backend currently returns 10-minute URLs. Prefer `resolveFileUrl`
 * for click handlers (it adds in-memory caching + backward compat).
 *
 * Returns: { url, s3Key, size, contentType, expiresIn }
 */
export async function presignFile(designId, s3Key) {
  const client = getApiClient();
  const response = await client.get(`/design/${designId}/presign`, {
    params: { s3Key },
  });
  return response.data;
}

// In-memory cache for presigned URLs (10min TTL to match backend presign expiry)
const urlCache = new Map();
const CACHE_TTL_MS = 10 * 60 * 1000;

/**
 * Resolve a file's download URL, preferring the cached `downloadUrl` field
 * when present (backward compat during rollout), otherwise fetching a fresh
 * presigned URL via `/design/{id}/presign`. Results are cached in-memory
 * with a 10min TTL so repeated clicks don't re-presign.
 *
 * Returns the URL string directly (not the full presignFile response).
 */
export async function resolveFileUrl(designId, file) {
  if (!file) return '';
  // Backward compat: when downloadUrl is present, use it directly
  if (file.downloadUrl) return file.downloadUrl;
  // Need an s3Key to fetch a fresh URL
  if (!file.s3Key || !designId) return '';
  // Check cache
  const cacheKey = file.s3Key;
  const cached = urlCache.get(cacheKey);
  if (cached && Date.now() < cached.expiresAt) {
    return cached.url;
  }
  // Fetch fresh presigned URL
  try {
    const result = await presignFile(designId, file.s3Key);
    if (result?.url) {
      urlCache.set(cacheKey, {
        url: result.url,
        expiresAt: Date.now() + CACHE_TTL_MS,
      });
      return result.url;
    }
    return '';
  } catch (err) {
    console.warn('[resolveFileUrl] presign failed for', file.s3Key, err);
    return '';
  }
}

// retryDesign / regenerate were removed when the steered chat agent
// replaced the legacy autonomous pipeline. The agent re-runs stages
// interactively — there's no longer a "redo the whole thing" button.
export async function exportToLucid(designId, { mermaidSource } = {}) {
  const client = getApiClient();
  // Forward the live canvas Mermaid source when the caller has it.
  // The backend prefers this over the stale S3 journey_v{N}.json so
  // user-edited diagrams export as they appear on the canvas, not as
  // they were when the pipeline first wrote the JSON.
  const body = mermaidSource ? { mermaidSource } : {};
  const response = await client.post(`/design/${designId}/export-to-lucid`, body);
  return response.data;
}

// Persist user's canvas edits. Server stores XML in S3 and stamps the
// design row with `lucidDiagramS3Key` so subsequent exports prefer the
// saved version over a freshly-built one.
export async function saveDiagramXml(designId, drawioXml) {
  const client = getApiClient();
  const response = await client.post(`/design/${designId}/export-to-lucid`, {
    action: 'save',
    drawioXml,
  });
  return response.data;
}

export async function setAgentGuidance(designId, stage, guidance) {
  const client = getApiClient();
  const response = await client.post(`/design/${designId}/guidance`, { stage, guidance });
  return response.data;
}

export async function listDesigns(consultantId) {
  const client = getApiClient();
  const params = consultantId ? { consultantId } : {};
  const response = await client.get('/design', { params });
  return response.data;
}

export async function deleteDesign(designId) {
  const client = getApiClient();
  const response = await client.delete(`/design/${designId}`);
  return response.data;
}

export async function updateDesign(designId, updates) {
  const client = getApiClient();
  const response = await client.put(`/design/${designId}`, updates);
  return response.data;
}

/**
 * Request cancellation of an in-flight generation turn. The backend sets
 * a `cancelRequested` flag on the design row; the agent's tool loop checks
 * this flag between stages and emits a `cancelled` SSE event before stopping.
 */
export async function cancelDesignGeneration(designId) {
  const client = getApiClient();
  const response = await client.put(`/design/${designId}`, { cancelRequested: true });
  return response.data;
}

export async function healthCheck() {
  const config = getConfig();
  const response = await axios.get(`${config.apiUrl}/health`);
  return response.data;
}

/**
 * Record a user-declared hours-saved estimate for a design (the form-entry
 * twin of the agent's post-export chat question). Backend emits an
 * hours_saved_declared usage event (source=form).
 */
export async function recordHoursSaved(designId, hoursSaved, freeText = '') {
  const client = getApiClient();
  const response = await client.post('/usage/hours-saved', { designId, hoursSaved, freeText });
  return response.data;
}

/**
 * Mirror a feedback submission into the shared Agentic App Portal metrics
 * tables. Fire-and-forget — callers must .catch(() => {}) so a portal-side
 * failure never surfaces to the user; the S3 feedback bundle is the source
 * of truth for our own inbox.
 */
export async function recordPortalFeedback({ customerName, type = 'feedback', feedbackText = '' } = {}) {
  const client = getApiClient();
  const response = await client.post('/usage/feedback-portal', { customerName, type, feedbackText });
  return response.data;
}

/**
 * Admin usage analytics rollup. Requires membership in one of the
 * backend's ADMIN_GROUPS (403 otherwise). Dates are YYYY-MM-DD; groupBy
 * is 'region' | 'project'.
 */
export async function fetchAdminUsage({ from, to, groupBy = 'region' } = {}) {
  const client = getApiClient();
  const params = new URLSearchParams({ groupBy });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const response = await client.get(`/admin/usage?${params.toString()}`);
  return response.data;
}

/**
 * Admin feedback inbox — user-submitted /feedback bundles (description,
 * category, context, attachment count), newest first. Same admin gate
 * as /admin/usage. Returns `{ items: [...], total }`.
 */
export async function fetchAdminFeedback({ limit = 100 } = {}) {
  const client = getApiClient();
  const response = await client.get(`/admin/feedback?limit=${limit}`);
  return response.data;
}

/**
 * Set the triage status (new | in-progress | resolved | wont-fix) and/or
 * re-classify the category (bug | feature | ux | other) of one feedback
 * bundle. `s3Prefix` comes verbatim from fetchAdminFeedback items.
 */
export async function setAdminFeedbackStatus({ s3Prefix, status, category, area } = {}) {
  const client = getApiClient();
  const response = await client.post('/admin/feedback/status', { s3Prefix, status, category, area });
  return response.data;
}

/**
 * List the consultant's knowledge-base documents (per-user, shared
 * across every project that consultant creates). The same set of
 * docs shows up in NewProjectModal and ProjectSettingsModal — the
 * canonical store is `s3://workflows-bucket/{consultantId}/kb/*`.
 * Returns `{ files: [{ name, s3Key, size, lastModified, downloadUrl }] }`.
 *
 * Falls back to `{ files: [] }` if the endpoint isn't deployed yet so
 * older callers don't crash on a 404.
 */
export async function listKnowledgeBase() {
  const client = getApiClient();
  try {
    const response = await client.get('/kb');
    return response.data;
  } catch (err) {
    if (err?.response?.status === 404) return { files: [] };
    throw err;
  }
}

export async function deleteKnowledgeBaseFile(s3Key) {
  const client = getApiClient();
  // POST instead of DELETE so the body (s3Key) survives API Gateway's
  // body-stripping for DELETE methods on some configurations.
  const response = await client.post('/kb/delete', { s3Key });
  return response.data;
}

// ─── KB Documents API (new /kb/documents contract) ───────────────────────────

/**
 * List KB documents for a consultant.
 * GET /kb/documents?consultantId={cid}
 * Returns { documents: [{ name, s3Key, sizeBytes, lastModified, ingestStatus }] }
 */
export async function listKbDocuments(consultantId) {
  const client = getApiClient();
  const response = await client.get('/kb/documents', { params: { consultantId } });
  return response.data;
}

/**
 * Request a presigned S3 PUT URL for a KB document upload.
 * POST /kb/documents/upload
 * Returns { uploadUrl, s3Key }
 */
export async function requestKbUploadUrl(consultantId, fileName, contentType) {
  const client = getApiClient();
  const response = await client.post('/kb/documents/upload', { consultantId, fileName, contentType });
  return response.data;
}

/**
 * Notify the backend that the S3 PUT completed so it can start ingestion.
 * POST /kb/documents/upload-complete
 * Returns { jobId }
 */
export async function notifyKbUploadComplete(s3Key) {
  const client = getApiClient();
  const response = await client.post('/kb/documents/upload-complete', { s3Key });
  return response.data;
}

/**
 * Delete a KB document and kick off de-indexing.
 * DELETE /kb/documents
 * Returns { deleted: true, jobId }
 */
export async function deleteKbDocument(s3Key) {
  const client = getApiClient();
  const response = await client.delete('/kb/documents', { data: { s3Key } });
  return response.data;
}

export async function deleteWorkflowFile(s3Key) {
  const client = getApiClient();
  const response = await client.post('/files/delete', { s3Key });
  return response.data;
}

/**
 * Persist the user's BlockNote canvas state. Each call writes a new
 * versioned canvas_v{N}.json under outputs/canvas/ and updates the
 * design row's pipeline.canvasS3Key so a refresh restores it.
 * Frontend debounces calls (~1.5s after typing stops) to avoid hammering.
 */
export async function saveCanvasBlocks(designId, blocks) {
  const client = getApiClient();
  const response = await client.post(`/design/${designId}/blocks`, { blocks });
  return response.data;
}

/**
 * PNG-only sync on the same route (no new API Gateway resource — the dev
 * API is CFN-orphaned): body without `blocks` skips the canvas write and
 * stores content-addressed journey PNGs under outputs/diagrams/ for the
 * server DOCX exporter. See services/journeyPngSync.ts.
 */
export async function uploadJourneyPngs(designId, journeyPngs) {
  const client = getApiClient();
  const response = await client.post(`/design/${designId}/blocks`, { journeyPngs });
  return response.data;
}


// ─── Per-consultant design-document template ────────────────────────────────
// Templates live separately from KB. The agent reads the DOCX directly
// via S3 (structure-preserving) and falls back to the global template
// when the consultant has no override.

export async function getDesignTemplate(consultantId) {
  const client = getApiClient();
  const response = await client.get('/template', { params: { consultantId } });
  return response.data; // { override: {...}|null, global: {...}|null }
}

export async function requestTemplateUploadUrl(consultantId, contentType) {
  const client = getApiClient();
  const response = await client.post('/template/upload', { consultantId, contentType });
  return response.data; // { uploadUrl, s3Key }
}

export async function compileDesignTemplate(consultantId) {
  const client = getApiClient();
  const response = await client.post('/template/upload', { consultantId, action: 'compile' });
  return response.data; // { compiled: true, sections, headings, tables, activatedKey, previousVersionKey }
}

export async function deleteDesignTemplate(consultantId) {
  const client = getApiClient();
  const response = await client.delete('/template', { data: { consultantId } });
  return response.data; // { deleted: true, structureReverted }
}

export async function renameWorkflowFile(s3Key, newName) {
  const client = getApiClient();
  const response = await client.post('/files/rename', { s3Key, newName });
  return response.data;
}

/**
 * Upload a file into the consultant's KB. Mirrors uploadDocument's
 * 5MB-direct vs presigned-URL split, but sets `kb: true` so the
 * upload-handler routes the file to `{consultantId}/kb/` instead of
 * the workflow inputs/ tree. The agent's KB lookup then sees these
 * docs across every project.
 */
export async function uploadKnowledgeBaseFile(file, consultantId, consultantEmail) {
  const MAX_DIRECT_UPLOAD_SIZE = 5 * 1024 * 1024;
  const client = getApiClient();
  if (file.size > MAX_DIRECT_UPLOAD_SIZE) {
    const presigned = await client.post('/upload', {
      requestPresignedUrl: true,
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      consultantId,
      consultantEmail,
      kb: true,
    });
    const { documentId, s3Key, uploadUrl } = presigned.data;
    const uploadResponse = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
    });
    if (!uploadResponse.ok) {
      throw new Error(`Upload failed (HTTP ${uploadResponse.status})`);
    }
    return { documentId, s3Key };
  }
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onload = async () => {
      try {
        const base64Content = reader.result.split(',')[1];
        const response = await client.post('/upload', {
          fileName: file.name,
          fileContent: base64Content,
          fileType: file.type,
          consultantId,
          consultantEmail,
          kb: true,
        });
        resolve(response.data);
      } catch (e) { reject(e); }
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * Preview an inline edit — sends the selected text + instruction to the
 * inline-edit-agent Lambda and gets back an AI-suggested replacement +
 * rationale. The frontend renders this as a diff card in the chat panel
 * so the user can Accept/Reject before any server-side state changes.
 *
 * payload shape: { blockId, oldText, oldHtml?, instruction, blockContext? }
 * response shape: { replacement, replacementHtml, rationale, editId }
 */
export async function previewInlineEdit(designId, payload) {
  const client = getApiClient();
  // Bedrock converse on a single short turn typically returns in 2-5s, but
  // give it 30s headroom in case of model warm-up. Using the default 60s
  // axios timeout from getApiClient() is fine; we don't override here.
  const response = await client.post(`/design/${designId}/inline-edit/preview`, payload);
  return response.data;
}

/**
 * Commit accumulated inline edits — sends the full pending batch to the
 * inline-edit-agent Lambda which patches the canonical HTML in S3 and
 * writes a new micro-version (e.g. v2 -> v2.1). Backend may report some
 * edits as failed (response.failedEdits[]); the caller is expected to
 * revert those optimistic patches in the iframe.
 *
 * edits shape: [{ editId, blockId, oldText, newText, newHtml, instruction }, ...]
 * response shape: { success, currentVersion, microVersion, newPreviewUrl,
 *                    appliedEdits, failedEdits }
 */
export async function commitInlineEdits(designId, edits) {
  const client = getApiClient();
  const response = await client.post(`/design/${designId}/inline-edit/commit`, { edits });
  return response.data;
}

/**
 * Build the data-plane URL for the dedicated inline-edit AgentCore
 * Runtime. Accepts either a full HTTPS URL or a bedrock-agentcore
 * runtime ARN (matches the chat agent's `getAgentCoreUrl` shape).
 * Returns '' when no ARN/URL is configured.
 */
function getInlineEditAgentUrl() {
  try {
    const cfg = getConfig() || {};
    const raw = String(cfg.inlineEditAgentArn || cfg.inlineEditAgentUrl || '');
    if (!raw) return '';
    if (raw.startsWith('https://') || raw.startsWith('http://')) return raw;
    // ARN → data-plane URL (same encoding the chat client uses).
    const arnMatch = raw.match(/^arn:aws:bedrock-agentcore:([a-z0-9-]+):/);
    if (!arnMatch) return '';
    const region = arnMatch[1];
    return `https://bedrock-agentcore.${region}.amazonaws.com/runtimes/${encodeURIComponent(raw)}/invocations?qualifier=DEFAULT`;
  } catch (_) {
    return '';
  }
}

/**
 * Preview an inline edit. Routing preference, in order:
 *   1. Dedicated inline-edit AgentCore Runtime (config.inlineEditAgentArn).
 *      Okta JWT validated at the AgentCore infrastructure layer — no
 *      Lambda resource policy to be wiped by Adobe SSM Automation.
 *   2. API Gateway buffered SSE route (`/design/{id}/inline-edit/preview-stream`)
 *      with the standard Okta JWT authorizer. Slower than AgentCore but
 *      always reachable as a fallback.
 *
 * The legacy Lambda Function URL path (config.inlineEditStreamUrl) was
 * REMOVED 2026-05-14. It used `AuthType: NONE` + `Principal: *` which
 * Adobe SSM Automation periodically wiped, surfacing as recurring 403s
 * in the inline-edit popover. Both surviving paths authenticate via
 * the Okta JWT and have no resource policy for SSM to scan.
 *
 * Each path emits the SAME SSE event shape (chunk / done / error) so the
 * caller doesn't care which one served the request.
 *
 * payload shape: { blockId, oldText, oldHtml?, instruction, blockContext? }
 *
 * Callbacks:
 *   onChunk({ text })                              — each Bedrock text delta
 *   onDone({ replacement, replacementHtml, rationale, editId })
 *   onError({ error })
 */
export async function previewInlineEditStream(designId, payload, { onChunk, onDone, onError }) {
  const config = getConfig();
  const token = await getAccessTokenJwt();

  // Primary URL — AgentCore Runtime if configured, else fall straight to
  // the API Gateway route (no Function URL middle layer).
  const agentCoreUrl = getInlineEditAgentUrl();
  const apiBase = (config.apiUrl || '').replace(/\/$/, '');
  const apiGwUrl = apiBase
    ? `${apiBase}/design/${encodeURIComponent(designId)}/inline-edit/preview-stream`
    : '';

  const primaryUrl = agentCoreUrl || apiGwUrl;
  // Fallback only when AgentCore is the primary AND we have an API GW
  // URL to fall back to. If AgentCore isn't configured, the API GW IS
  // the primary — no fallback below it.
  const fallbackUrl = agentCoreUrl && apiGwUrl ? apiGwUrl : null;

  async function fetchOnce(targetUrl, includeDesignIdInBody) {
    const fullBody = includeDesignIdInBody ? { ...payload, designId } : payload;
    return fetch(targetUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(fullBody),
    });
  }

  let response;
  try {
    response = await fetchOnce(primaryUrl, Boolean(agentCoreUrl));
  } catch (networkErr) {
    // AgentCore unreachable — try the API Gateway fallback before
    // surfacing the error to the user.
    if (fallbackUrl) {
      try {
        response = await fetchOnce(fallbackUrl, false);
      } catch (fbErr) {
        onError({ error: fbErr?.message || networkErr?.message || 'network error' });
        return;
      }
    } else {
      onError({ error: networkErr?.message || 'network error' });
      return;
    }
  }

  if (!response.ok && fallbackUrl) {
    // 5xx / 403 from AgentCore — degrade to the API Gateway route.
    try {
      response = await fetchOnce(fallbackUrl, false);
    } catch (_) {
      // fall through to the !response.ok check below with the original
      // response.
    }
  }

  if (!response.ok) {
    let errMsg = `HTTP ${response.status}`;
    try {
      const errBody = await response.json();
      errMsg = errBody?.error || errBody?.message || errMsg;
    } catch (_) { /* ignore */ }
    onError({ error: errMsg });
    return;
  }

  // Read the response body as a stream of SSE text frames.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  function processBuffer() {
    // SSE frames are separated by \n\n. Each frame has lines:
    //   event: <name>
    //   data: <json>
    const frames = buffer.split('\n\n');
    // The last element may be a partial frame — keep it in the buffer.
    buffer = frames.pop() ?? '';
    for (const frame of frames) {
      if (!frame.trim()) continue;
      let eventName = 'message';
      let dataLine = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) {
          eventName = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
          dataLine = line.slice('data:'.length).trim();
        }
      }
      if (!dataLine) continue;
      let parsed;
      try { parsed = JSON.parse(dataLine); } catch (_) { continue; }
      if (eventName === 'chunk') {
        onChunk(parsed);
      } else if (eventName === 'done') {
        onDone(parsed);
      } else if (eventName === 'error') {
        onError(parsed);
      }
    }
  }

  // Pump the ReadableStream until done.
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      processBuffer();
    }
    // Flush any trailing bytes.
    buffer += decoder.decode();
    processBuffer();
  } catch (streamErr) {
    onError({ error: streamErr?.message || 'stream read error' });
  }
}
